/**
 * Iter 118 — Country name → flag emoji helper.
 *
 * Backend's slip-leg payload includes a `country` field (e.g., "Spain",
 * "Portugal", "England") sourced from API-Sports.  We render the
 * corresponding flag emoji next to the league name on the betslip
 * card so users can identify competitions at a glance.
 *
 * Why an inline lookup table instead of a library:
 *   • The set of football-relevant countries is small (~80 distinct
 *     values across API-Sports' league pool), so a hand-maintained
 *     map costs less than pulling in `country-flag-emoji` (200KB).
 *   • The backend's strings already match ISO English names — no
 *     ambiguity around localisation.
 *   • We can return "" for unknown countries and the UI gracefully
 *     omits the flag rather than rendering a tofu glyph.
 */

// Mapping: API-Sports country name (English) → ISO-3166-1 alpha-2.
// Sorted alphabetically by country for readability.  When you encounter
// a new value in production logs, just add a line here.
const NAME_TO_ISO2 = {
  Afghanistan: "AF",
  Albania: "AL",
  Algeria: "DZ",
  Andorra: "AD",
  Angola: "AO",
  Argentina: "AR",
  Armenia: "AM",
  Australia: "AU",
  Austria: "AT",
  Azerbaijan: "AZ",
  Bahrain: "BH",
  Bangladesh: "BD",
  Belarus: "BY",
  Belgium: "BE",
  Bolivia: "BO",
  "Bosnia and Herzegovina": "BA",
  Bosnia: "BA",
  Botswana: "BW",
  Brazil: "BR",
  Bulgaria: "BG",
  "Burkina Faso": "BF",
  Cambodia: "KH",
  Cameroon: "CM",
  Canada: "CA",
  Chile: "CL",
  China: "CN",
  "China PR": "CN",
  Colombia: "CO",
  "Costa Rica": "CR",
  Croatia: "HR",
  Cyprus: "CY",
  "Czech Republic": "CZ",
  Czechia: "CZ",
  Denmark: "DK",
  "Dominican Republic": "DO",
  Ecuador: "EC",
  Egypt: "EG",
  "El Salvador": "SV",
  England: "GB-ENG",
  Estonia: "EE",
  Ethiopia: "ET",
  "Faroe Islands": "FO",
  Finland: "FI",
  France: "FR",
  Georgia: "GE",
  Germany: "DE",
  Ghana: "GH",
  Greece: "GR",
  Guatemala: "GT",
  Honduras: "HN",
  "Hong Kong": "HK",
  Hungary: "HU",
  Iceland: "IS",
  India: "IN",
  Indonesia: "ID",
  Iran: "IR",
  Iraq: "IQ",
  Ireland: "IE",
  Israel: "IL",
  Italy: "IT",
  "Ivory Coast": "CI",
  Jamaica: "JM",
  Japan: "JP",
  Jordan: "JO",
  Kazakhstan: "KZ",
  Kenya: "KE",
  Kosovo: "XK",
  Kuwait: "KW",
  Kyrgyzstan: "KG",
  Latvia: "LV",
  Lebanon: "LB",
  Libya: "LY",
  Liechtenstein: "LI",
  Lithuania: "LT",
  Luxembourg: "LU",
  Macedonia: "MK",
  "North Macedonia": "MK",
  Malaysia: "MY",
  Malta: "MT",
  Mexico: "MX",
  Moldova: "MD",
  Montenegro: "ME",
  Morocco: "MA",
  Netherlands: "NL",
  "New Zealand": "NZ",
  Nicaragua: "NI",
  Nigeria: "NG",
  "Northern Ireland": "GB-NIR",
  Norway: "NO",
  Oman: "OM",
  Pakistan: "PK",
  Panama: "PA",
  Paraguay: "PY",
  Peru: "PE",
  Philippines: "PH",
  Poland: "PL",
  Portugal: "PT",
  Qatar: "QA",
  Romania: "RO",
  Russia: "RU",
  "Saudi Arabia": "SA",
  Scotland: "GB-SCT",
  Senegal: "SN",
  Serbia: "RS",
  Singapore: "SG",
  Slovakia: "SK",
  Slovenia: "SI",
  "South Africa": "ZA",
  "South Korea": "KR",
  "Korea Republic": "KR",
  Spain: "ES",
  Sweden: "SE",
  Switzerland: "CH",
  Syria: "SY",
  Taiwan: "TW",
  Tajikistan: "TJ",
  Tanzania: "TZ",
  Thailand: "TH",
  Tunisia: "TN",
  Turkey: "TR",
  "Türkiye": "TR",
  Turkmenistan: "TM",
  Uganda: "UG",
  Ukraine: "UA",
  "United Arab Emirates": "AE",
  "United Kingdom": "GB",
  "United States": "US",
  USA: "US",
  Uruguay: "UY",
  Uzbekistan: "UZ",
  Venezuela: "VE",
  Vietnam: "VN",
  Wales: "GB-WLS",
  Yemen: "YE",
  Zambia: "ZM",
  Zimbabwe: "ZW",
  // International / non-country competitions — render a neutral globe
  // so we still get a leading glyph instead of nothing.
  World: "_GLOBE_",
  International: "_GLOBE_",
  Europe: "_GLOBE_",
  Africa: "_GLOBE_",
  Asia: "_GLOBE_",
  "South America": "_GLOBE_",
  "North America": "_GLOBE_",
};

// Special-case glyphs for sub-national + non-country entries.
const SPECIAL_GLYPHS = {
  "GB-ENG": "\u{1F3F4}\u{E0067}\u{E0062}\u{E0065}\u{E006E}\u{E0067}\u{E007F}", // 🏴󠁧󠁢󠁥󠁮󠁧󠁿
  "GB-SCT": "\u{1F3F4}\u{E0067}\u{E0062}\u{E0073}\u{E0063}\u{E0074}\u{E007F}", // 🏴󠁧󠁢󠁳󠁣󠁴󠁿
  "GB-WLS": "\u{1F3F4}\u{E0067}\u{E0062}\u{E0077}\u{E006C}\u{E0073}\u{E007F}", // 🏴󠁧󠁢󠁷󠁬󠁳󠁿
  "GB-NIR": "🇬🇧", // No standard emoji for Northern Ireland; fall back to UK
  XK: "🇽🇰",     // Kosovo (not in unicode flag set everywhere but renders on most modern systems)
  _GLOBE_: "🌍",
};

const _iso2ToEmoji = (iso2) => {
  if (!iso2) return "";
  if (SPECIAL_GLYPHS[iso2]) return SPECIAL_GLYPHS[iso2];
  if (iso2.length !== 2) return "";
  // Regional Indicator Symbols: 'A' (U+1F1E6) + (charCode - 65)
  const A_OFFSET = 0x1f1e6 - 65;
  const a = iso2.charCodeAt(0);
  const b = iso2.charCodeAt(1);
  if (a < 65 || a > 90 || b < 65 || b > 90) return "";
  return String.fromCodePoint(a + A_OFFSET) + String.fromCodePoint(b + A_OFFSET);
};

/**
 * Return the flag emoji for a country name as supplied by API-Sports.
 * Returns "" when the country is unknown — callers should render the
 * leg without a leading glyph rather than a tofu.
 *
 * @param {string|null|undefined} name
 * @returns {string}
 */
export function flagForCountry(name) {
  if (!name) return "";
  const trimmed = String(name).trim();
  if (!trimmed) return "";
  const iso2 = NAME_TO_ISO2[trimmed];
  if (!iso2) return "";
  return _iso2ToEmoji(iso2);
}

// Exposed for testing only.
export const __test = { NAME_TO_ISO2, _iso2ToEmoji };
